<template>
  <!-- Pre-footer gradient transition -->
  <div class="relative h-24 bg-gradient-to-b from-gray-50 via-gray-100 to-transparent"></div>
  
  <footer class="relative overflow-hidden">
    <!-- Glassmorphism background with hero-style gradients -->
    <div class="absolute inset-0 bg-gray-50">
      <!-- Gradient blur circles matching hero -->
      <div class="absolute top-0 right-0 w-[40%] h-[40%] rounded-full bg-primary-200/30 blur-3xl"></div>
      <div class="absolute bottom-0 left-0 w-[50%] h-[50%] rounded-full bg-secondary-200/30 blur-3xl"></div>
      <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[35%] h-[35%] rounded-full bg-purple-200/25 blur-3xl"></div>
    </div>
    
    <!-- Glassmorphism overlay -->
    <div class="absolute inset-0 bg-white/40 backdrop-blur-xl"></div>
    
    <div class="container-custom relative z-10 text-gray-900">
      <div class="py-16">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Company Info -->
          <div class="col-span-1 md:col-span-2">
            <div class="mb-6">
              <img 
                src="@/assets/logo/logo_horizntal.png" 
                alt="Ruxzom Logo" 
                class="h-10 w-auto"
              />
            </div>
            <p class="text-gray-900 mb-4 max-w-md text-lg font-semibold">
              {{ companyContent.tagline || 'Transforming Ideas into Digital Reality' }}
            </p>
            <p class="text-gray-600 text-sm mb-8 max-w-md leading-relaxed">
              We are a cutting-edge software development company specializing in custom web applications, 
              mobile solutions, and enterprise software that drives business growth.
            </p>
            <!-- Social Links -->
            <div class="flex space-x-3">
              <a 
                v-for="social in socialLinks" 
                :key="social.name"
                :href="social.href" 
                target="_blank"
                rel="noopener noreferrer"
                class="group w-11 h-11 bg-white/60 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-gradient-to-br hover:from-primary-600 hover:to-purple-600 transition-all duration-300 shadow-lg hover:shadow-primary-500/50 hover:scale-110 border border-gray-200/50"
              >
                <component :is="social.icon" class="w-5 h-5 text-gray-700 group-hover:text-white group-hover:scale-110 transition-all duration-300" />
                <span class="sr-only">{{ social.name }}</span>
              </a>
            </div>
          </div>
          <!-- Quick Links -->
          <div>
            <h3 class="font-heading font-semibold text-lg mb-6 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Quick Links</h3>
            <ul class="space-y-3">
              <li v-for="link in quickLinks" :key="link.name">
                <router-link 
                  :to="link.href"
                  class="text-gray-600 hover:text-gray-900 transition-all duration-200 flex items-center group font-medium"
                >
                  <span class="w-0 group-hover:w-2 h-0.5 bg-gradient-to-r from-primary-500 to-purple-500 mr-0 group-hover:mr-2 transition-all duration-300"></span>
                  {{ link.name }}
                </router-link>
              </li>
            </ul>
          </div>
          
          <!-- Services -->
          <div>
            <h3 class="font-heading font-semibold text-lg mb-6 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Services</h3>
            <ul class="space-y-3">
              <li v-if="services.length === 0">
                <span class="text-gray-500 text-sm">Loading services...</span>
              </li>
              <li v-for="service in services" :key="service.id">
                <router-link 
                  :to="{ name: 'ServiceDetail', params: { id: service.uid } }"
                  class="text-gray-600 hover:text-gray-900 transition-all duration-200 flex items-center group font-medium"
                >
                  <span class="w-0 group-hover:w-2 h-0.5 bg-gradient-to-r from-primary-500 to-purple-500 mr-0 group-hover:mr-2 transition-all duration-300"></span>
                  {{ service.name }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <!-- Contact Info -->
      <div class="border-t border-gray-300/50 py-10">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div class="flex items-center space-x-4 group">
            <div class="w-12 h-12 bg-gradient-to-br from-primary-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30 group-hover:shadow-primary-500/50 transition-all duration-300 group-hover:scale-110">
              <EmailIcon class="w-6 h-6 text-white" />
            </div>
            <div>
              <p class="text-sm text-gray-500 mb-1 font-medium">Email</p>
              <a :href="`mailto:${contactContent.email || 'support@ruxzom.com'}`" class="text-gray-900 font-semibold hover:text-primary-600 transition-colors">{{ contactContent.email || 'support@ruxzom.com' }}</a>
            </div>
          </div>
          
          <div class="flex items-center space-x-4 group cursor-pointer" @click="orderStore.openWhatsApp('Hi! I need help/support.')">
            <div class="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:shadow-green-500/50 transition-all duration-300 group-hover:scale-110">
              <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </div>
            <div>
              <p class="text-sm text-gray-500 mb-1 font-medium">WhatsApp</p>
              <p class="text-gray-900 font-semibold hover:text-green-600 transition-colors">{{ contactContent.whatsapp || '+8801974611004' }}</p>
            </div>
          </div>

          <div class="flex items-center space-x-4 group">
            <div class="w-12 h-12 bg-gradient-to-br from-primary-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30 group-hover:shadow-primary-500/50 transition-all duration-300 group-hover:scale-110">
              <PhoneIcon class="w-6 h-6 text-white" />
            </div>
            <div>
              <p class="text-sm text-gray-500 mb-1 font-medium">Phone</p>
              <p class="text-gray-900 font-semibold">{{ contactContent.phone || '+8801974611004' }}</p>
            </div>
          </div>
          
          <div class="flex items-center space-x-4 group">
            <div class="w-12 h-12 bg-gradient-to-br from-primary-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30 group-hover:shadow-primary-500/50 transition-all duration-300 group-hover:scale-110">
              <LocationIcon class="w-6 h-6 text-white" />
            </div>
            <div>
              <p class="text-sm text-gray-500 mb-1 font-medium">Address</p>
              <p class="text-gray-900 font-semibold text-sm">{{ contactContent.address || 'Mymensingh , Bangladesh' }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Copyright -->
      <div class="border-t border-gray-300/50 py-8">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <p class="text-gray-600 text-sm">
            © {{ currentYear }} <span class="bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent font-semibold">Ruxzom</span>. All rights reserved.
          </p>
          <div class="flex space-x-6 mt-4 md:mt-0">
            <router-link to="/privacy-policy" class="text-gray-600 hover:text-gray-900 text-sm transition-colors duration-200 hover:underline decoration-primary-500 font-medium">
              Privacy Policy
            </router-link>
            <router-link to="/terms-of-service" class="text-gray-600 hover:text-gray-900 text-sm transition-colors duration-200 hover:underline decoration-primary-500 font-medium">
              Terms of Service
            </router-link>
            <router-link to="/data-deletion-policy" class="text-gray-600 hover:text-gray-900 text-sm transition-colors duration-200 hover:underline decoration-primary-500 font-medium">
              Data Deletion Policy
            </router-link>
            <router-link to="/cookie-policy" class="text-gray-600 hover:text-gray-900 text-sm transition-colors duration-200 hover:underline decoration-primary-500 font-medium">
              Cookie Policy
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useSettingsStore } from '@/stores/settings'
import { useServiceStore } from '@/stores/services'
import { useOrderStore } from '@/stores/orders'
import { 
  EmailIcon, 
  PhoneIcon, 
  LocationIcon, 
  LinkedInIcon, 
  TwitterIcon, 
  GitHubIcon, 
  FacebookIcon 
} from '@/assets/icons'

const settingsStore = useSettingsStore()
const servicesStore = useServiceStore()
const orderStore = useOrderStore()

// Load settings and services when component mounts
onMounted(async () => {
  await settingsStore.fetchSettings()
  
  // Only fetch services if the store is empty
  if (servicesStore.services.length === 0) {
    try {
      await servicesStore.fetchServices()
    } catch (error) {
      console.error('Error loading services for footer:', error)
    }
  }
})

// Get services from store (limited to 5 for footer)
const services = computed(() => {
  return servicesStore.getActiveServices().slice(0, 5)
})

// Current year for copyright
const currentYear = computed(() => new Date().getFullYear())

// Get contact content from settings
const contactContent = computed(() => settingsStore.getContactInfo())

// Get company content from settings
const companyContent = computed(() => settingsStore.getCompanyInfo())

// Navigation links
const quickLinks = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Services', href: '/services' },
  { name: 'Portfolio', href: '/portfolio' },
  { name: 'Contact', href: '/contact' }
]

const socialLinks = computed(() => {
  const social = settingsStore.getSocialLinks()
  const links = []
  
  if (social.linkedin) {
    links.push({ name: 'LinkedIn', href: social.linkedin, icon: LinkedInIcon })
  }
  if (social.twitter) {
    links.push({ name: 'Twitter', href: social.twitter, icon: TwitterIcon })
  }
  if (social.github) {
    links.push({ name: 'GitHub', href: social.github, icon: GitHubIcon })
  }
  if (social.facebook) {
    links.push({ name: 'Facebook', href: social.facebook, icon: FacebookIcon })
  }
  
  return links
})

</script>
